import { AgGridReact } from "ag-grid-react";
import * as React from "react";
import { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ColDef, GetRowIdParams, GridOptions, IsRowSelectable, RowNode } from "ag-grid-community";
import { ID_BILLABLE, ID_BILLED, ID_REJECTED, ID_REPLACED, ID_SENT } from "../constants/EnelxStatuses";
import dayjs from "dayjs";
import { Box, IconButton, Stack, Tooltip } from "@mui/material";
import { DownloadRounded, SendRounded } from "@mui/icons-material";
import ColouredChip from "../../../component/ColouredChip";
import { COLOR_BILLABLE, COLOR_BILLED, COLOR_REJECTED, COLOR_REPLACED, COLOR_SENT } from "../constants/EnelxColors";
import { formatDate } from "../../../utils/FormatUtils";
import AgGridCustomTable from "../../../../../util/component/AgGridCustomTable";
import EmptyState from "../../../../../util/component/EmptyState";
import {ExportDataAsExcelBase} from "../../../utils/ExportDataAsExcelBase";

const ReportPreviewTable = (props: any) => {
    const { t } = useTranslation('finance', { keyPrefix: 'enelx' });

    const gridRef = useRef<AgGridReact>(null);

    const columnDefs : ColDef[] = [
        {
            field: 'validation', headerName: '', headerTooltip: t('tables.recharge.validation'),
            suppressSizeToFit: true, maxWidth: 65,
            sortable: false,
            checkboxSelection: true, showDisabledCheckboxes: true, headerCheckboxSelection: true,
            lockVisible: true, lockPinned: true,
        },
        {
            field: 'rfidCard', headerName: t('tables.recharge.rfid-card'), headerTooltip: t('tables.recharge.rfid-card'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'sessionStart', headerName: t('tables.recharge.session'), headerTooltip: t('tables.recharge.session'),
            minWidth: 200, unSortIcon: true,
            valueFormatter: params => formatDate(params.value)
        },
        {
            field: 'plate', headerName: t('tables.recharge.plate'), headerTooltip: t('tables.recharge.plate'),
            minWidth: 200, unSortIcon: false, sortable: false
        },
        {
            field: 'chargingDuration', headerName: t('tables.recharge.recharge-duration'), headerTooltip: t('tables.recharge.recharge-duration'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'province', headerName: t('tables.recharge.recharge-place'), headerTooltip: t('tables.recharge.recharge-place'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'useType', headerName: t('tables.recharge.station-type'), headerTooltip: t('tables.recharge.station-type'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'totalEnergy', headerName: t('tables.recharge.energy-total'), headerTooltip: t('tables.recharge.energy-total'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'customerName', headerName: t('tables.recharge.customer'), headerTooltip: t('tables.recharge.customer'),
            minWidth: 200, unSortIcon: true
        },
        {
            field: 'customerTaxCode', headerName: t('tables.recharge.customer-tax-code'), headerTooltip: t('tables.recharge.customer-tax-code'),
            minWidth: 200, unSortIcon: false, sortable: false
        },
        {
            field: 'raCode', headerName: t('tables.recharge.ra-code'), headerTooltip: t('tables.recharge.ra-code'),
            minWidth: 200, unSortIcon: false, sortable: false
        },
        {
            field: 'status', headerName: t('tables.recharge.status'), headerTooltip: t('tables.recharge.status'),
            minWidth: 150, unSortIcon: true,
            pinned: "right", lockPosition: "right", lockVisible: true, lockPinned: true,
            cellRendererSelector: (params) => {
                const status = params.data.status;
                switch (status) {
                    case ID_BILLABLE:
                        return {
                            component: () => <ColouredChip name={ID_BILLABLE} color={COLOR_BILLABLE}/>
                        };
                    case ID_BILLED:
                        return {
                            component: () => <ColouredChip name={ID_BILLED} color={COLOR_BILLED}/>
                        };
                    case ID_REJECTED:
                        return {
                            component: () => <ColouredChip name={ID_REJECTED} color={COLOR_REJECTED} tooltip={t(`import.rejected-reasons.${params.data.errorMessage}`)}/>
                        };
                    case ID_REPLACED:
                        return {
                            component: () => <ColouredChip name={ID_REPLACED} color={COLOR_REPLACED}/>
                        }
                    case ID_SENT:
                        return {
                            component: () => <ColouredChip name={ID_SENT} color={COLOR_SENT}/>
                        }
                    default:
                        break;
                }
            }
        },
        {
            field: 'status-reason', headerName: t('tables.recharge.status-reason'),
            hide: true, suppressColumnsToolPanel: true, suppressFiltersToolPanel: true,
        },
    ];

    const gridOptions: GridOptions = {
        getRowId: (params: GetRowIdParams) => params.data['id'],
        defaultColDef: {
            sortable: true,
            resizable: true,
            wrapHeaderText: false,
        },
        noRowsOverlayComponentParams: { title: t('tables.recharge.no-rows-overlay.title') }
    };

    const rejectedRows = props.data.filter((it: any) => it.status === ID_REJECTED).length;

    const isRowSelectable = useMemo<IsRowSelectable>(() => {
        return (rowNode: RowNode) => {
            let status = rowNode.data.status;
            return status === ID_BILLABLE;
        };
    }, []);

    function getExportableExcelColumns(): string[] {
        let keys: string[] = gridRef?.current?.columnApi.getAllDisplayedColumns().map(column => column.getColId()) as string[];
        let amountIndex: number = keys.findIndex(column => column === 'status');
        keys.splice(amountIndex + 1, 0, 'status-reason');
        return keys;
    }

    function exportAsExcel() {
        gridRef?.current?.api.exportDataAsExcel({
            ...ExportDataAsExcelBase,
            author: "Finance - Payments",
            fileName: `${props.data['0']?.reportName} - ${dayjs().unix()}.xlsx`,
            sheetName: `${props.data['0']?.reportName}`,
            columnKeys: getExportableExcelColumns(),

            processCellCallback: params => {
                if (params.column.getColId() === "status-reason") {
                    return t(`import.rejected-reasons.${params.node?.data.errorMessage}`);
                }
                if (params.column.getColId() === "sessionStart") {
                    return formatDate(params.value);
                }
                return params.value;
            },
            shouldRowBeSkipped: params => params.node.data.status !== ID_REJECTED,
        });
    }

    const noRowsOverlayComponent = useMemo<any>(() => {
        return EmptyState;
    }, []);

    const toolbar = {
        renderInfo: () => (<></>
        ),
        renderActions: () => (
                <Stack direction={'row'} spacing={3}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                        <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{t('preview.buttons.export-excel.title') + '\n' + t('preview.buttons.export-excel.tooltip', {
                            rejectedRows: rejectedRows
                        })}</div>} arrow>
                            <IconButton disabled={props.data.length === 0 || rejectedRows === 0}
                                        color={'accent'}
                                        onClick={exportAsExcel}>
                                <DownloadRounded/>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{t('preview.buttons.send-bill.title') + '\n' + t('preview.buttons.send-bill.tooltip', {
                            selectedRows: props.selectedDataLength
                        })}</div>} arrow>
                            <IconButton disabled={props.data.length === 0 || props.selectedDataLength === 0}
                                        color={'accent'}
                                        onClick={props.onInvoiceClick}>
                                <SendRounded/>
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Stack>
        )
    }
    return (
            <AgGridCustomTable
                    toolbar={toolbar}
                    gridRef={gridRef} columnDefs={columnDefs} gridOptions={gridOptions}
                    rowData={props.data} rowBuffer={0}
                    rowSelection={'multiple'} isRowSelectable={isRowSelectable}
                    suppressRowClickSelection
                    noRowsOverlayComponent={noRowsOverlayComponent}
                    onGridReady={() => {
                        gridRef.current!.api.sizeColumnsToFit();
                        gridRef.current!.api.hideOverlay();
                    }}
                    onFirstDataRendered={() => {
                        gridRef.current!.api.selectAll();
                    }}
                    onRowSelected={(event: any) => {
                        props.onRowSelected(event.node.data.id, event.node.isSelected(), event.node.data);
                    }}
                    onSelectionChanged={() => {
                        props.setSelectedDataLength(gridRef.current!.api.getSelectedRows().length);
                    }}
            />
    );
}

export default ReportPreviewTable;